import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Package, Search, Eye, PenSquare, Trash2, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface Product {
  id: string;
  name: string;
  sku: string;
  description: string;
  regularPrice: number;
  stockQuantity: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  mainImage?: string;
  images?: string[];
  categories?: string[];
  category?: string; // Categoria singular (usado na ediu00e7u00e3o)
  companyId: string;
  createdAt: string | { seconds: number; nanoseconds: number };
  createdBy: string;
  status: 'pending';
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
  woocommerce?: {
    last_synced: string;
    woocommerce_id: number;
  };
}

export default function PendingProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    // Configurar listener para produtos pendentes
    const pendingProductsRef = collection(db, 'pendingProducts');
    const pendingQuery = query(pendingProductsRef);

    const unsubPending = onSnapshot(pendingQuery, snapshot => {
      // Processar todos os produtos
      const allPendingProducts = snapshot.docs.map(doc => {
        const data = doc.data();
        const createdAt = typeof data.createdAt === 'string' 
          ? data.createdAt 
          : data.createdAt?.seconds 
            ? new Date(data.createdAt.seconds * 1000).toISOString()
            : new Date().toISOString();
        
        return {
          id: doc.id,
          ...data,
          createdAt,
          status: 'pending' as const
        };
      });

      // Filtrar por categoria se necessário
      const filteredProducts = selectedCategory && selectedCategory.trim() !== ''
        ? allPendingProducts.filter(product => {
            const matchesCategory = product.category === selectedCategory;
            const matchesCategoriesArray = product.categories && 
              Array.isArray(product.categories) && 
              product.categories.includes(selectedCategory);
            return matchesCategory || matchesCategoriesArray;
          })
        : allPendingProducts;

      // Ordenar e atualizar o estado
      setProducts(filteredProducts.sort((a, b) => 
        new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime()
      ));

      // Atualizar loading state
      setLoading(false);
    });

    // Carregar categorias uma vez
    loadCategories();

    // Cleanup function
    return () => {
      unsubPending();
    };
  }, [currentUser, navigate, selectedCategory]); // Incluir selectedCategory para recriar o listener quando a categoria mudar

  // Atualizar URL quando mudar a categoria ou pu00e1gina
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedCategory) {
      params.set('category', selectedCategory);
    } else {
      params.delete('category');
    }
    params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [selectedCategory, currentPage]);

  // Atualizar estados quando mudar a URL
  useEffect(() => {
    const category = searchParams.get('category') || '';
    const page = parseInt(searchParams.get('page') || '1');
    setSelectedCategory(category);
    setCurrentPage(page);
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      setCategories(categoriesData);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  // Função loadPendingProducts removida pois agora usamos onSnapshot

  const handleDeleteProduct = async (product: Product) => {
    try {
      await deleteDoc(doc(db, 'pendingProducts', product.id));
      setProducts(products.filter(p => p.id !== product.id));
      toast.success('Produto removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover produto:', err);
      toast.error('Erro ao remover produto');
    }
  };

  const handleApproveProduct = async (product: Product) => {
    try {
      // Criar na coleção de produtos aprovados
      const { id, status, ...productData } = product;
      await setDoc(doc(db, 'products', id), {
        ...productData,
        status: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser?.uid
      });

      // Remover da coleção de pendentes
      await deleteDoc(doc(db, 'pendingProducts', id));
      
      setProducts(products.filter(p => p.id !== id));
      toast.success('Produto aprovado com sucesso!');
    } catch (err) {
      console.error('Erro ao aprovar produto:', err);
      toast.error('Erro ao aprovar produto');
    }
  };

  const formatDate = (date: string | { seconds: number; nanoseconds: number }) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleString('pt-PT');
    }
    return new Date(date.seconds * 1000).toLocaleString('pt-PT');
  };

  const getCategoryNames = (categoryIds: string[] | undefined | string) => {
    // Se for uma string (categoria singular), converte para array
    if (typeof categoryIds === 'string') {
      categoryIds = [categoryIds];
    }
    
    if (!categoryIds || (Array.isArray(categoryIds) && categoryIds.length === 0)) return '-';
    
    // Garante que categoryIds seja tratado como array
    const idsArray = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    
    return idsArray.map(id => {
      const category = categories.find(cat => cat.id === id);
      return category ? category.name : id;
    }).join(', ');
  };

  const filteredProducts = products.filter(product => {
    // Aplicamos apenas o filtro de pesquisa por texto, pois o filtro por categoria
    // ju00e1 u00e9 aplicado diretamente na consulta ao Firebase
    return (
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Implementau00e7u00e3o da paginau00e7u00e3o
  const itemsPerPage = 25; // 25 produtos por pu00e1gina
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!currentUser?.role === 'admin') {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Produtos Pendentes</h1>
          </div>

          {products.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Pesquisar por nome ou SKU..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <div className="flex-shrink-0">
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1); // Reset para primeira pu00e1gina ao mudar categoria
                    }}
                    className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          {products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum produto pendente</h3>
              <p className="text-gray-500">
                Não existem produtos aguardando aprovação no momento.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categorias
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Criação
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {product.mainImage ? (
                            <img
                              src={product.mainImage}
                              alt={product.name}
                              className="h-10 w-10 rounded-md object-cover mr-3"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-gray-100 flex items-center justify-center mr-3">
                              <Package className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          <span>{product.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{product.sku}</td>
                      <td className="px-6 py-4">
                        €{product.regularPrice.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        {product.stockQuantity}
                      </td>
                      <td className="px-6 py-4">
                        {getCategoryNames(product.categories || product.category)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(product.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/dashboard/products/pending/${product.id}`)}
                            className="text-primary hover:text-primary-dark"
                            title="Ver detalhes"
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            onClick={() => navigate(`/dashboard/products/pending/${product.id}/edit`)}
                            className="text-primary hover:text-primary-dark"
                            title="Editar produto"
                          >
                            <PenSquare size={20} />
                          </button>
                          <button
                            onClick={() => handleApproveProduct(product)}
                            className="text-green-600 hover:text-green-700"
                            title="Aprovar produto"
                          >
                            <CheckCircle2 size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="text-red-600 hover:text-red-700"
                            title="Remover produto"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Controles de Paginau00e7u00e3o */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center gap-3 mt-4 pb-4">
                  <div className="text-sm text-gray-700">
                    <span>Mostrando </span>
                    <span className="font-medium">{indexOfFirstProduct + 1}</span>
                    <span> a </span>
                    <span className="font-medium">{Math.min(indexOfLastProduct, filteredProducts.length)}</span>
                    <span> de </span>
                    <span className="font-medium">{filteredProducts.length}</span>
                    <span> produtos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      «
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      ‹
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(number => {
                          if (totalPages <= 7) return true;
                          if (number === 1 || number === totalPages) return true;
                          if (number >= currentPage - 2 && number <= currentPage + 2) return true;
                          return false;
                        })
                        .map((number, index, array) => (
                          <React.Fragment key={number}>
                            {index > 0 && array[index - 1] !== number - 1 && (
                              <span className="px-2 py-1 text-gray-500">...</span>
                            )}
                            <button
                              onClick={() => paginate(number)}
                              className={`px-3 py-1 rounded-md min-w-[2.25rem] ${
                                currentPage === number
                                  ? 'bg-primary text-white font-medium'
                                  : 'bg-white hover:bg-gray-50 text-gray-500 border'
                              }`}
                            >
                              {number}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      ›
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      »
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
